/**
 * Modified version of useFilters from react-table for tables with expandable parent/subRows.
 *
 * Standard useFilters applies column filters to parent rows first, which breaks day-grouped
 * tables where parent row values differ from subRow values (e.g. day label vs event name).
 *
 * This hook filters subRows first, then removes parent rows that have no matching subRows left.
 */
import React from 'react'

import { getFirstDefined, getFilterMethod, shouldAutoRemoveFilter } from 'react-table/src/utils'

import { actions, functionalUpdate, useGetLatest, useMountedLayoutEffect } from 'react-table'

import * as filterTypes from 'react-table/src/filterTypes'

export const useHierarchicalFilters = hooks => {
  hooks.stateReducers.push(reducer)
  hooks.useInstance.push(useInstance)
}

// Keep plugin name so useRowSelect plugin order checks still pass.
useHierarchicalFilters.pluginName = 'useFilters'

function reducer(state, action, previousState, instance) {
  if (action.type === actions.init) {
    return {
      filters: [],
      ...state,
    }
  }

  if (action.type === actions.resetFilters) {
    return {
      ...state,
      filters: instance.initialState.filters || [],
    }
  }

  if (action.type === actions.setFilter) {
    const { columnId, filterValue } = action
    const { allColumns, filterTypes: userFilterTypes } = instance

    const column = allColumns.find(d => d.id === columnId)

    if (!column) {
      throw new Error(`React-Table: Could not find a column with id: ${columnId}`)
    }

    const filterMethod = getFilterMethod(column.filter, userFilterTypes || {}, filterTypes)

    const previousfilter = state.filters.find(d => d.id === columnId)

    const newFilter = functionalUpdate(filterValue, previousfilter && previousfilter.value)

    if (shouldAutoRemoveFilter(filterMethod.autoRemove, newFilter, column)) {
      return {
        ...state,
        filters: state.filters.filter(d => d.id !== columnId),
      }
    }

    if (previousfilter) {
      return {
        ...state,
        filters: state.filters.map(d => {
          if (d.id === columnId) {
            return { id: columnId, value: newFilter }
          }
          return d
        }),
      }
    }

    return {
      ...state,
      filters: [...state.filters, { id: columnId, value: newFilter }],
    }
  }

  if (action.type === actions.setAllFilters) {
    const { filters } = action
    const { allColumns, filterTypes: userFilterTypes } = instance

    return {
      ...state,
      filters: functionalUpdate(filters, state.filters).filter(filter => {
        const column = allColumns.find(d => d.id === filter.id)
        const filterMethod = getFilterMethod(column.filter, userFilterTypes || {}, filterTypes)

        if (shouldAutoRemoveFilter(filterMethod.autoRemove, filter.value, column)) {
          return false
        }
        return true
      }),
    }
  }
}

function useInstance(instance) {
  const {
    data,
    rows,
    flatRows,
    rowsById,
    allColumns,
    filterTypes: userFilterTypes,
    manualFilters,
    defaultCanFilter = false,
    disableFilters,
    state: { filters },
    dispatch,
    autoResetFilters = true,
  } = instance

  const setFilter = React.useCallback(
    (columnId, filterValue) => {
      dispatch({ type: actions.setFilter, columnId, filterValue })
    },
    [dispatch],
  )

  const setAllFilters = React.useCallback(
    filters => {
      dispatch({
        type: actions.setAllFilters,
        filters,
      })
    },
    [dispatch],
  )

  allColumns.forEach(column => {
    const { id, accessor, defaultCanFilter: columnDefaultCanFilter, disableFilters: columnDisableFilters } = column

    column.canFilter = accessor
      ? getFirstDefined(
          columnDisableFilters === true ? false : undefined,
          disableFilters === true ? false : undefined,
          true,
        )
      : getFirstDefined(columnDefaultCanFilter, defaultCanFilter, false)

    column.setFilter = val => setFilter(column.id, val)

    const found = filters.find(d => d.id === id)
    column.filterValue = found && found.value
  })

  const [filteredRows, filteredFlatRows, filteredRowsById] = React.useMemo(() => {
    if (manualFilters || !filters.length) {
      return [rows, flatRows, rowsById]
    }

    const filteredFlatRows = []
    const filteredRowsById = {}

    const filterRows = (rows, depth = 0) => {
      let filteredRows = rows.map(row => ({
        ...row,
        subRows: row.subRows && row.subRows.length > 0 ? filterRows(row.subRows, depth + 1) : row.subRows,
      }))

      if (depth === 0) {
        filteredRows = filteredRows.filter(row => row.subRows?.length)
      } else {
        filteredRows = filters.reduce((filteredSoFar, { id: columnId, value: filterValue }) => {
          const column = allColumns.find(d => d.id === columnId)

          if (!column) {
            return filteredSoFar
          }

          const filterMethod = getFilterMethod(column.filter, userFilterTypes || {}, filterTypes)

          if (!filterMethod) {
            console.warn(`Could not find a valid 'column.filter' for column with the ID: ${column.id}.`)
            return filteredSoFar
          }

          column.filteredRows = filterMethod(filteredSoFar, [columnId], filterValue)

          return column.filteredRows
        }, filteredRows)
      }

      filteredRows.forEach(row => {
        filteredFlatRows.push(row)
        filteredRowsById[row.id] = row
      })

      return filteredRows
    }

    return [filterRows(rows), filteredFlatRows, filteredRowsById]
  }, [manualFilters, filters, rows, flatRows, rowsById, allColumns, userFilterTypes])

  React.useMemo(() => {
    const nonFilteredColumns = allColumns.filter(column => !filters.find(d => d.id === column.id))

    nonFilteredColumns.forEach(column => {
      column.preFilteredRows = filteredRows
      column.filteredRows = filteredRows
    })
  }, [filteredRows, filters, allColumns])

  const getAutoResetFilters = useGetLatest(autoResetFilters)

  useMountedLayoutEffect(() => {
    if (getAutoResetFilters()) {
      dispatch({ type: actions.resetFilters })
    }
  }, [dispatch, manualFilters ? null : data])

  Object.assign(instance, {
    preFilteredRows: rows,
    preFilteredFlatRows: flatRows,
    preFilteredRowsById: rowsById,
    filteredRows,
    filteredFlatRows,
    filteredRowsById,
    rows: filteredRows,
    flatRows: filteredFlatRows,
    rowsById: filteredRowsById,
    setFilter,
    setAllFilters,
  })
}
